import './Impact.css';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import IntegrationBadges from '../components/IntegrationBadges';

const API_BASE = 'https://dashboard-dot-primeval-gear-417120.ue.r.appspot.com';
const getCompanyId = (c) => c?.CompanyId || c?.companyId || c?.company_id || c?.id || '';

function fmtMoney(v) {
  if (v == null || isNaN(Number(v))) return '--';
  return '$' + Math.round(Number(v)).toLocaleString();
}

// ---------------------------------------------------------------------------
// Mock data. The backend endpoints (/impact/summary, /impact/plays,
// /impact/methodology) are being built in parallel. The fetch calls below are
// wired to the real routes; when they fail or 404 we fall back to these
// mocks so the page can be reviewed end to end.
// ---------------------------------------------------------------------------
// Shape matches api-master impact/routes.py /impact/summary (impact/MOUNT.md,
// FRONTEND NOTE): the total covers current measurement windows, NOT a calendar month.
const MOCK_SUMMARY = {
  window_start: '2026-06-22',
  window_end: '2026-07-19',
  window_basis: 'current measurement windows (day-14 per one_time play, this week per always_on play)',
  total_correlated_revenue: 18450,
  total_correlated_orders: 96,
  klaviyo_confirmed: 6120,
  plays_run: 5,
  campaigns_sent: 9,
  plan_cost: null,
};

const MOCK_PLAYS = [
  {
    play_id: 'winback-core',
    play_name: 'Win-back Lapsed High Spenders',
    description: 'High spenders who went quiet in the last 90 days, invited back with a personal offer.',
    activated_at: '2026-06-15T00:00:00+00:00',
    play_type: 'always_on',
    status: 'performing',
    day: 34,
    revenue_7d: 2140,
    revenue_14d: 4310,
    orders_7d: 18,
    orders_14d: 39,
    klaviyo_attributed: 1650,
    weekly: [
      { week: '2026-06-22', revenue: 1480 },
      { week: '2026-06-29', revenue: 1720 },
      { week: '2026-07-06', revenue: 1890 },
      { week: '2026-07-13', revenue: 2140 },
    ],
    campaigns: [{ campaign_id: 'c1', campaign_name: 'Winback wave 3' }],
  },
  {
    play_id: 'second-purchase',
    play_name: 'Second Purchase Nudge',
    description: 'First-time buyers nudged toward their second order inside the window when they are most likely to come back.',
    activated_at: '2026-05-29T00:00:00+00:00',
    play_type: 'always_on',
    status: 'stalled',
    day: 51,
    revenue_7d: 120,
    revenue_14d: 480,
    orders_7d: 2,
    orders_14d: 6,
    klaviyo_attributed: 90,
    weekly: [
      { week: '2026-06-22', revenue: 610 },
      { week: '2026-06-29', revenue: 520 },
      { week: '2026-07-06', revenue: 360 },
      { week: '2026-07-13', revenue: 120 },
    ],
    campaigns: [],
  },
  {
    play_id: 'vip-early-access',
    play_name: 'VIP Early Access Drop',
    description: 'Top 5% customers by spend got first access to the new drop before anyone else.',
    activated_at: '2026-07-13T00:00:00+00:00',
    play_type: 'one_time',
    status: 'measuring',
    day: 6,
    revenue_7d: 980,
    revenue_14d: 980,
    orders_7d: 11,
    orders_14d: 11,
    klaviyo_attributed: 410,
    weekly: [],
    campaigns: [{ campaign_id: 'c2', campaign_name: 'VIP early access' }],
  },
  {
    play_id: 'dormant-spring',
    play_name: 'Dormant Reactivation Spring',
    description: 'Customers with no purchase in 6+ months, reactivated with a limited spring offer.',
    activated_at: '2026-07-05T00:00:00+00:00',
    play_type: 'one_time',
    status: 'final',
    day: 14,
    revenue_7d: 0,
    revenue_14d: 3260,
    orders_7d: 0,
    orders_14d: 27,
    klaviyo_attributed: 1180,
    weekly: [],
    campaigns: [{ campaign_id: 'c3', campaign_name: 'Spring reactivation' }],
  },
];

const MOCK_METHODOLOGY = {
  title: 'How we measure this',
  sections: [
    {
      heading: 'Correlated revenue',
      body: 'We compare purchases from the customers in each Play audience during the measurement window against their baseline behavior. Revenue shown is the total purchased by those customers after the Play launched. It is a correlation, not a controlled experiment.',
    },
    {
      heading: 'Klaviyo confirmed revenue',
      body: 'Revenue that Klaviyo attributes to direct clicks and opens on the campaigns connected to each Play. It is a subset of correlated revenue and the most conservative number on this page.',
    },
    {
      heading: 'Measurement windows',
      body: 'One-time Plays are measured for 14 days after launch, then marked final. Always-on Plays are tracked week over week; a Play is flagged as declining or stalled when its weekly revenue trend drops.',
    },
  ],
};

// ---------------------------------------------------------------------------

const STATUS_LABELS = {
  measuring: 'Measuring',
  final: 'Final',
  performing: 'Performing',
  declining: 'Declining',
  stalled: 'Stalled',
};

function StatusBadge({ status }) {
  return (
    <span className={`impact-badge impact-badge--${status}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

// Tiny inline sparkline for the 4-week trend (no chart lib needed).
function TrendSparkline({ weekly }) {
  if (!weekly || weekly.length < 2) return null;
  const values = weekly.map((w) => Number(w.revenue) || 0);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const W = 120;
  const H = 32;
  const step = W / (values.length - 1);
  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(H - 4 - ((v - min) / range) * (H - 8)).toFixed(1)}`)
    .join(' ');
  return (
    <svg className="impact-sparkline" width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
      <polyline points={points} fill="none" stroke="#6B2EFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DisabledButton({ label }) {
  return (
    <span className="impact-disabled-btn-wrap" title="Coming soon">
      <button type="button" className="impact-btn impact-btn--disabled" disabled>
        {label}
      </button>
    </span>
  );
}

function AlwaysOnCard({ play }) {
  const weekly = play.weekly || [];
  const current = weekly.length ? Number(weekly[weekly.length - 1].revenue) || 0 : Number(play.revenue_7d) || 0;
  const previous = weekly.length > 1 ? Number(weekly[weekly.length - 2].revenue) || 0 : null;
  const delta = previous != null && previous > 0 ? ((current - previous) / previous) * 100 : null;
  return (
    <div className="impact-card" id={`play-${play.play_id}`}>
      <div className="impact-card-top">
        <div>
          <div className="impact-card-name">{play.play_name}</div>
          {play.description && <p className="impact-card-desc">{play.description}</p>}
          <div className="impact-card-sub">Always on · day {play.day}</div>
        </div>
        <StatusBadge status={play.status} />
      </div>
      <div className="impact-card-body">
        <div className="impact-card-stats">
          <div className="impact-card-stat impact-card-stat--klaviyo">
            <div className="impact-card-stat-value">{fmtMoney(play.klaviyo_attributed)}</div>
            <div className="impact-card-stat-label">Confirmed by Klaviyo</div>
          </div>
          <div className="impact-card-stat impact-card-stat--ours">
            <div className="impact-card-stat-value">{fmtMoney(current)}</div>
            <div className="impact-card-stat-label">
              We measured this week
              {previous != null && (
                <span className={`impact-delta ${delta != null && delta < 0 ? 'impact-delta--down' : 'impact-delta--up'}`}>
                  {' '}vs {fmtMoney(previous)}
                </span>
              )}
            </div>
          </div>
        </div>
        <TrendSparkline weekly={weekly} />
      </div>
      {play.status === 'stalled' && (
        <div className="impact-card-foot">
          <span className="impact-card-actions">
            <DisabledButton label="Adjust" />
            <DisabledButton label="Pause" />
          </span>
        </div>
      )}
    </div>
  );
}

function MeasuringCard({ play }) {
  return (
    <div className="impact-card" id={`play-${play.play_id}`}>
      <div className="impact-card-top">
        <div>
          <div className="impact-card-name">{play.play_name}</div>
          {play.description && <p className="impact-card-desc">{play.description}</p>}
          <div className="impact-card-sub">Measuring: day {play.day} of 14</div>
        </div>
        <StatusBadge status={play.status} />
      </div>
      <div className="impact-card-body">
        <div className="impact-card-stats">
          <div className="impact-card-stat impact-card-stat--klaviyo">
            <div className="impact-card-stat-value">{fmtMoney(play.klaviyo_attributed)}</div>
            <div className="impact-card-stat-label">Confirmed by Klaviyo</div>
          </div>
          <div className="impact-card-stat impact-card-stat--ours">
            <div className="impact-card-stat-value">{fmtMoney(play.revenue_7d)}</div>
            <div className="impact-card-stat-label">We measured so far · {play.orders_7d ?? 0} orders</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CompletedRow({ play }) {
  return (
    <div className="impact-completed-row" id={`play-${play.play_id}`}>
      <div className="impact-completed-info">
        <div className="impact-card-name">{play.play_name}</div>
        {play.description && <p className="impact-card-desc">{play.description}</p>}
        <div className="impact-completed-line">
          <span className="impact-inline-klaviyo">{fmtMoney(play.klaviyo_attributed)}</span> confirmed by Klaviyo
          {' · '}
          <span className="impact-inline-ours">{fmtMoney(play.revenue_14d)}</span> measured by us
          {' · '}{play.orders_14d ?? 0} orders in 14 days
        </div>
      </div>
      <div className="impact-card-actions">
        <DisabledButton label="Repeat" />
        <DisabledButton label="Archive" />
      </div>
    </div>
  );
}

function ImpactTable({ plays }) {
  const [sortKey, setSortKey] = useState('ours');
  const [sortDir, setSortDir] = useState('desc');
  const rows = (plays || []).map((p) => {
    const isFinal = p.status === 'final';
    const ours = isFinal
      ? Number(p.revenue_14d) || 0
      : (p.play_type === 'always_on' && p.weekly && p.weekly.length
          ? Number(p.weekly[p.weekly.length - 1].revenue) || 0
          : Number(p.revenue_7d) || 0);
    const orders = isFinal ? (p.orders_14d ?? 0) : (p.orders_7d ?? 0);
    const klaviyo = Number(p.klaviyo_attributed) || 0;
    const windowLabel = isFinal
      ? '14-day final'
      : (p.play_type === 'always_on' ? 'This week' : `Measuring · day ${p.day}`);
    const startRaw = (p.weekly && p.weekly.length && p.weekly[0].week) ? p.weekly[0].week : '';
    let startLabel = '—';
    if (startRaw) {
      const d = new Date(`${startRaw}T00:00:00`);
      if (!Number.isNaN(d.getTime())) {
        startLabel = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
    } else if (p.day != null) {
      startLabel = `Day ${p.day}`;
    }
    return { p, name: p.play_name || '', klaviyo, ours, orders, windowLabel, start: startRaw, startLabel };
  });
  const sorted = [...rows].sort((a, b) => {
    let av;
    let bv;
    if (sortKey === 'name') { av = a.name.toLowerCase(); bv = b.name.toLowerCase(); }
    else { av = a[sortKey]; bv = b[sortKey]; }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });
  const toggle = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'name' ? 'asc' : 'desc'); }
  };
  const arrow = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
  return (
    <div className="impact-table-wrap">
      <table className="impact-table">
        <thead>
          <tr>
            <th className="impact-th impact-th--text" onClick={() => toggle('name')}>Play{arrow('name')}</th>
            <th className="impact-th impact-th--text" onClick={() => toggle('start')}>Started{arrow('start')}</th>
            <th className="impact-th impact-th--num impact-th--klaviyo" onClick={() => toggle('klaviyo')}>Klaviyo revenue{arrow('klaviyo')}</th>
            <th className="impact-th impact-th--num impact-th--ours" onClick={() => toggle('ours')}>Our revenue{arrow('ours')}</th>
            <th className="impact-th impact-th--num" onClick={() => toggle('orders')}>Orders{arrow('orders')}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={r.p.play_id} className="impact-tr">
              <td className="impact-td impact-td--text">
                <div className="impact-td-name">{r.name}</div>
                <div className="impact-td-window">{r.windowLabel}</div>
              </td>
              <td className="impact-td impact-td--text impact-td--date">{r.startLabel}</td>
              <td className="impact-td impact-td--num impact-td--klaviyo">{fmtMoney(r.klaviyo)}</td>
              <td className="impact-td impact-td--num impact-td--ours">{fmtMoney(r.ours)}</td>
              <td className="impact-td impact-td--num">{r.orders}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MethodologyModal({ methodology, isMock, onClose }) {
  const data = methodology || MOCK_METHODOLOGY;
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="impact-modal-overlay" onClick={onClose}>
      <div className="impact-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-label={data.title}>
        <div className="impact-modal-head">
          <h3>{data.title}</h3>
          <button type="button" className="impact-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        {isMock && (
          <div className="impact-mock-note">Sample methodology text while measurement is being connected</div>
        )}
        {(data.sections || []).map((s, i) => (
          <div className="impact-modal-section" key={i}>
            <h4>{s.heading}</h4>
            <p>{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ImpactPage({ companyInfo, token, onNavigate }) {
  const companyId = getCompanyId(companyInfo);
  const [summary, setSummary] = useState(null);
  const [plays, setPlays] = useState(null); // null = loading
  const [methodology, setMethodology] = useState(null);
  const [filter, setFilter] = useState('all');
  const [timeRange, setTimeRange] = useState('all'); // '7' | '30' | '90' | 'all'
  const [search, setSearch] = useState('');
  const [showMethodology, setShowMethodology] = useState(false);
  const [usingMock, setUsingMock] = useState(false);
  // Separate flag: methodology can fall back to mock even when summary/plays are real.
  const [methodologyIsMock, setMethodologyIsMock] = useState(false);

  const load = useCallback(async () => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const q = `?company=${encodeURIComponent(companyId)}`;
    try {
      const [sRes, pRes, mRes] = await Promise.all([
        fetch(`${API_BASE}/impact/summary${q}`, { headers }),
        fetch(`${API_BASE}/impact/plays${q}&status=all`, { headers }),
        fetch(`${API_BASE}/impact/methodology`, { headers }),
      ]);
      if (!sRes.ok || !pRes.ok) throw new Error('impact API not available');
      setSummary(await sRes.json());
      const p = await pRes.json();
      setPlays(Array.isArray(p) ? p : p.plays || []);
      if (mRes.ok) {
        setMethodology(await mRes.json());
        setMethodologyIsMock(false);
      } else {
        // No unlabeled mocks: methodology falls back alone, flag it separately.
        setMethodology(MOCK_METHODOLOGY);
        setMethodologyIsMock(true);
      }
      setUsingMock(false);
    } catch (e) {
      // Backend is still being built: fall back to sample data.
      setSummary(MOCK_SUMMARY);
      setPlays(MOCK_PLAYS);
      setMethodology(MOCK_METHODOLOGY);
      setUsingMock(true);
      setMethodologyIsMock(true);
    }
  }, [companyId, token]);

  useEffect(() => { load(); }, [load]);

  // Deep link support: /impact#play-XYZ scrolls to that play once loaded.
  useEffect(() => {
    if (!plays) return;
    const hash = window.location.hash;
    if (hash && hash.startsWith('#play-')) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('impact-card--highlight');
        setTimeout(() => el.classList.remove('impact-card--highlight'), 2500);
      }
    }
  }, [plays]);

  // Time range (by activated_at, exposed per play card by impact/routes.py)
  // and free-text search (play name or connected campaign names).
  const filteredPlays = useMemo(() => {
    let list = plays || [];
    if (timeRange !== 'all') {
      const cutoff = Date.now() - Number(timeRange) * 86400000;
      list = list.filter((p) => {
        const t = p.activated_at ? Date.parse(p.activated_at) : NaN;
        return !isNaN(t) && t >= cutoff;
      });
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        if ((p.play_name || '').toLowerCase().includes(q)) return true;
        return (p.campaigns || []).some((c) => (c.campaign_name || '').toLowerCase().includes(q));
      });
    }
    return list;
  }, [plays, timeRange, search]);

  const { activeAlwaysOn, activeMeasuring, completed } = useMemo(() => {
    const list = filteredPlays;
    return {
      activeAlwaysOn: list.filter((p) => p.play_type === 'always_on' && p.status !== 'final'),
      activeMeasuring: list.filter((p) => p.play_type === 'one_time' && p.status === 'measuring'),
      completed: list.filter((p) => p.status === 'final'),
    };
  }, [filteredPlays]);

  const showActive = filter === 'all' || filter === 'active';
  const showCompleted = filter === 'all' || filter === 'completed';
  const hasPlays = (plays || []).length > 0;
  const hasFiltered = filteredPlays.length > 0;
  const TIME_RANGES = [
    ['7', 'Last 7 days'],
    ['30', 'Last 30 days'],
    ['90', 'Last 90 days'],
    ['all', 'All time'],
  ];

  return (
    <div className="impact-page-v2">
      <div className="impact-header">
        <IntegrationBadges companyInfo={companyInfo} token={token} />
        <h2 className="impact-title-main">Impact Tracker</h2>
        <p className="impact-intro">
          The revenue your activated Plays brought back. Every number here is measured against real orders in your store.
        </p>
        {summary && (
          <>
            {/* Label is "Measured correlated revenue", not "This month": the total
                covers the current measurement windows per play (backend decision
                2026-07-19, impact/MOUNT.md FRONTEND NOTE), not a calendar month. */}
            <div className="impact-hero-compare">
              <div className="impact-metric impact-metric--primary">
                <div className="impact-metric-amount">{fmtMoney(summary.klaviyo_confirmed)}</div>
                <div className="impact-metric-label">Confirmed by Klaviyo</div>
                <div className="impact-metric-sub">Direct clicks &amp; opens · most conservative</div>
              </div>
              <div className="impact-metric impact-metric--secondary">
                <div className="impact-metric-amount">{fmtMoney(summary.total_correlated_revenue)}</div>
                <div className="impact-metric-label">Measured by UPSELLerate</div>
                <div className="impact-metric-sub">Correlated with your real orders · broader view</div>
              </div>
            </div>
            {summary.plays_run != null && (
              <div className="impact-headline-sub">
                {summary.plays_run} plays run · {summary.campaigns_sent ?? 0} campaigns sent
              </div>
            )}
            {/* Intentional per spec: ROI vs subscription fee line. Only renders when plan_cost is real. */}
            {summary.plan_cost != null && Number(summary.plan_cost) > 0 && (
              <div className="impact-headline-sub">
                That is {(Number(summary.total_correlated_revenue) / Number(summary.plan_cost)).toFixed(1)}x your plan cost
              </div>
            )}
          </>
        )}
        {usingMock && <div className="impact-mock-note">Sample data shown while measurement is being connected</div>}
      </div>

      {plays === null ? (
        <div className="impact-loading">Loading impact data...</div>
      ) : !hasPlays ? (
        <div className="impact-empty">
          <div className="impact-empty-title">No plays measured yet</div>
          <p>When you launch your first Play, this is where you will see the revenue it produces.</p>
          <button type="button" className="impact-btn impact-btn--primary" onClick={() => onNavigate?.('revenue')}>
            Go to Revenue Plays
          </button>
        </div>
      ) : (
        <>
          <div className="impact-toolbar">
            <div className="impact-filter">
              {['all', 'active', 'completed'].map((f) => (
                <button
                  key={f}
                  type="button"
                  className={`impact-filter-btn ${filter === f ? 'active' : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f === 'all' ? 'All' : f === 'active' ? 'Active' : 'Completed'}
                </button>
              ))}
            </div>
            <div className="impact-filter">
              {TIME_RANGES.map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={`impact-filter-btn ${timeRange === value ? 'active' : ''}`}
                  onClick={() => setTimeRange(value)}
                >
                  {label}
                </button>
              ))}
            </div>
            <input
              type="search"
              className="impact-search"
              placeholder="Search plays or campaigns"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search plays or campaigns"
            />
          </div>

          {!hasFiltered && (
            <p className="impact-section-empty impact-filter-empty">
              No plays match these filters. Try a wider time range or clear the search.
            </p>
          )}

          {hasFiltered && (
            <div className="impact-section">
              <ImpactTable
                plays={
                  filter === 'completed'
                    ? completed
                    : filter === 'active'
                      ? [...activeAlwaysOn, ...activeMeasuring]
                      : filteredPlays
                }
              />
            </div>
          )}
        </>
      )}

      <div className="impact-footer">
        <button type="button" className="impact-link" onClick={() => setShowMethodology(true)}>
          How we measure this
        </button>
      </div>

      {showMethodology && (
        <MethodologyModal methodology={methodology} isMock={methodologyIsMock} onClose={() => setShowMethodology(false)} />
      )}
    </div>
  );
}
